/**
 * Centralized configuration for scroll effects and layout constants
 * 
 * This file contains all magic numbers and configuration values for the scroll
 * fade effects implementation, making them easily maintainable and configurable.
 */

export const SCROLL_EFFECTS_CONFIG = {
  /**
   * Scroll activation threshold in pixels
   * Controls when the fade effect starts appearing
   */
  ACTIVATION_THRESHOLD: 30,

  /**
   * Height of the fade gradient overlay in pixels
   * Determines how much content area gets the fade effect
   */
  FADE_HEIGHT: 80,

  /**
   * Offset from right edge to avoid scrollbar interference
   * Standard scrollbar width consideration
   */
  SCROLLBAR_OFFSET: 20,

  /**
   * Opacity values for the fade gradient
   */
  FADE_OPACITY: {
    /** Starting opacity at 70% of gradient height */
    START: 0.3,
    /** Ending opacity at 100% of gradient height */
    END: 0.8,
  },

  /**
   * Spacing values for layout consistency
   */
  SPACING: {
    /** Top margin for input container */
    INPUT_TOP: 12,
    /** Top margin for control elements */
    CONTROLS_TOP: 12,
    /** Footer padding values */
    FOOTER_PADDING: 18,
  },

  /**
   * Animation configuration
   */
  ANIMATION: {
    /** Transition duration in milliseconds */
    DURATION: 300,
    /** Transition timing function */
    EASING: 'ease-in-out' as const,
  },
} as const;

/**
 * CSS custom property names for theming
 * These map to CSS variables that can be overridden
 */
export const CSS_VARIABLES = {
  FADE_HEIGHT: '--scroll-fade-height',
  FADE_START: '--scroll-fade-start',
  FADE_END: '--scroll-fade-end',
  FADE_DURATION: '--scroll-fade-duration',
  SCROLLBAR_OFFSET: '--scrollbar-offset',
  INPUT_SPACING: '--input-spacing',
  FOOTER_SPACING: '--footer-spacing',
} as const;

/**
 * Type definitions for scroll configuration
 */
export interface ScrollPosition {
  readonly x: number;
  readonly y: number;
}

export interface ScrollEffectsState {
  position: ScrollPosition;
  isScrolling: boolean;
}

/**
 * Utility function to get CSS variable value with fallback
 */
export const getCSSVariable = (variableName: string, fallback: string | number): string => {
  if (typeof window === 'undefined') {
    return String(fallback);
  }
  
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
  
  return value || String(fallback);
};

/**
 * Helper to validate scroll threshold value
 */
export const validateScrollThreshold = (threshold: number): number => {
  return Math.max(0, Math.min(threshold, 100)); // Clamp between 0-100px
};